import { ref, watch } from 'vue';
import {
  useFlowRuleName,
  useI18n,
  usePlanLimits,
  type PlanLimitsProp,
} from '@/composables';
import { $emitter } from '@/services/emitter';
import { FlowHierarchyTypeEnum } from '@/services/models/Enums';
import { getFlowApiRequest } from '@/services/api/flows-api';

type FlowPreviewProps = {
  flow: Flow;
  updateFlow: (flow: Flow) => void;
};

export const useCampaignFlowPreview = (getProps: () => FlowPreviewProps) => {
  const { t } = useI18n();
  const { buildFlowRuleName, isOppositeFlowRulePredicate } = useFlowRuleName();
  const planLimitsControl = usePlanLimits();

  const loading = ref(false);
  const flowFull = ref<Flow | null>(null);
  const flowMap = ref<TreeViewItem[]>([]);
  const treeIdCounter = ref(0);

  const treeLeafs = [
    {
      index: 'landings',
      type: FlowHierarchyTypeEnum.landing,
      title: t('Landings'),
    },
    {
      index: 'offers',
      type: FlowHierarchyTypeEnum.offer,
      title: t('Offers'),
    },
  ];

  const weightAllowedGroups = [
    FlowHierarchyTypeEnum.path,
    FlowHierarchyTypeEnum.landing,
    FlowHierarchyTypeEnum.offer,
  ];

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const conditionPlanLimits = (condition: string) =>
    planLimitsControl.conditionPlanLimits(condition);

  const countWeight = (item: { weight: number; status?: string }, items: Array<{ weight: number; status?: string }>) => {
    let percent = 0;

    if (Number(item) !== 0) {
      let sum = 0;
      items.forEach((entry) => {
        if (entry.status === 'inactive') return;
        sum = sum + Number(entry.weight);
      });

      if (sum !== 0) {
        percent = (item.weight * 100) / sum;
      }
    }

    return Math.round(percent);
  };

  const getWeightOf = (weights: Array<{ weight: number }>, weight: number) => {
    const totalWeight = weights
      .map((item) => Number(item.weight))
      .reduce((accumulator, item) => accumulator + item, 0);

    if (totalWeight === 0) return 0;

    return Math.round((weight / totalWeight) * 100);
  };

  const translatedName = (item: { type?: string; name?: string }) => {
    const { name } = item;

    if (item.type === 'path') {
      return name?.replace('Path', t('Path')) || '';
    }
    if (name === 'Default') {
      return t('Default');
    }
    return name || '';
  };

  const showPlanLimitsWarning = (conditions: Record<string, unknown>) =>
    Boolean(
      Object.keys(conditions).find((condition) =>
        hasPlanLimitsRestriction(conditionPlanLimits(condition)),
      ),
    );

  const getNameItem = (
    id: string,
    items: Array<{ id: string; weight: number; nameFull?: string }>,
    type: FlowHierarchyTypeEnum,
  ): TreeViewItem => {
    const target = items.find((item) => item.id === id);

    return {
      id: (treeIdCounter.value = treeIdCounter.value + 1),
      type,
      weight: target?.weight,
      weightPercent: target ? getWeightOf(items, target.weight) : 0,
      name: target?.nameFull || '',
    };
  };

  const createTree = (
    data: Array<Record<string, unknown>>,
    type: FlowHierarchyTypeEnum = FlowHierarchyTypeEnum.rule,
  ): TreeViewItem[] => {
    const result: TreeViewItem[] = [];

    data.forEach((item) => {
      const element: TreeViewItem = {
        id: (treeIdCounter.value = treeIdCounter.value + 1),
        name: item.name as string,
        hasPlanLimitsRestriction: item.conditions
          ? showPlanLimitsWarning(item.conditions as Record<string, unknown>)
          : false,
        type,
        weight: item.weight as number,
        weightPercent:
          type === FlowHierarchyTypeEnum.path
            ? countWeight(item as { weight: number; status?: string }, data as Array<{ weight: number; status?: string }>)
            : 0,
        conditions: item.conditions
          ? buildFlowRuleName(
              { conditions: item.conditions as Record<string, unknown> },
              false,
            )
          : null,
        children: [],
      };

      if (item.status === 'inactive') {
        element.inactive = true;
      } else if (Array.isArray(item.paths) && item.paths.length) {
        element.children = createTree(item.paths as Array<Record<string, unknown>>, FlowHierarchyTypeEnum.path);
      } else {
        treeLeafs.forEach((subItem) => {
          if (item.directLinking && subItem.type === FlowHierarchyTypeEnum.landing) {
            return;
          }

          const group: TreeViewItem = {
            id: (treeIdCounter.value = treeIdCounter.value + 1),
            type: FlowHierarchyTypeEnum.utility,
            name: subItem.title,
            children: [],
          };

          const groupItems = item[subItem.index] as Array<{ id: string; weight: number; nameFull?: string }> | undefined;

          if (groupItems?.length) {
            groupItems.forEach((entry) => {
              if (entry.id !== '') {
                group.children?.push(getNameItem(entry.id, groupItems, subItem.type));
              }
            });

            if (group.children?.length) {
              element.children?.push(group);
            } else {
              treeIdCounter.value = treeIdCounter.value - 1;
            }
          }
        });
      }

      result.push(element);
    });

    return result;
  };

  const isShowWeight = (type: FlowHierarchyTypeEnum) => weightAllowedGroups.includes(type);

  const loadFlowPreview = async () => {
    const { flow } = getProps();
    if (!flow?.id) return;

    loading.value = true;
    treeIdCounter.value = 0;

    flowFull.value = (await getFlowApiRequest(flow.id)) as Flow;
    flowMap.value = flowFull.value?.rules
      ? createTree(flowFull.value.rules as Array<Record<string, unknown>>)
      : [];

    loading.value = false;
  };

  const openEditFlowDialog = () => {
    const { flow, updateFlow } = getProps();
    $emitter.emit('open-dialog', {
      name: 'FlowDialog',
      data: {
        mode: 'edit',
        id: flow.id,
        onConfirm: (updatedFlow: Flow) => {
          updateFlow(updatedFlow);
        },
      },
    });
  };

  watch(
    () => getProps().flow,
    () => {
      loadFlowPreview();
    },
    { immediate: true },
  );

  return {
    t,
    loading,
    flowMap,
    translatedName,
    isOppositeFlowRulePredicate,
    isShowWeight,
    openEditFlowDialog,
  };
};
